import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import MyWork from "@/pages/MyWork";
import Tasks from "@/pages/Tasks";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import CalendarPage from "@/pages/CalendarPage";
import Chat from "@/pages/Chat";
import TimeTracking from "@/pages/TimeTracking";
import Requests from "@/pages/Requests";
import Leave from "@/pages/Leave";
import Notes from "@/pages/Notes";
import Meetings from "@/pages/Meetings";
import Designs from "@/pages/Designs";
import Purchasing from "@/pages/Purchasing";
import Reports from "@/pages/Reports";
import SettingsPage from "@/pages/SettingsPage";
import AdminPanel from "@/pages/AdminPanel";
import VideoMeeting from "@/pages/VideoMeeting";

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-950">
      <div className="flex flex-col items-center gap-3">
        <img src="/icon-192.png" alt="WorkHub" className="h-14 w-14 rounded-xl animate-pulse" />
        <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
      </div>
    </div>
  );
}

function Guard({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function ModuleGuard({ moduleKey, children }) {
  const { canAccess } = useAuth();
  if (!canAccess(moduleKey)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { can } = useAuth();
  if (!can("manage_users")) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route element={<Guard><AppLayout /></Guard>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<AdminGuard><AdminPanel /></AdminGuard>} />
            <Route path="/video" element={<VideoMeeting />} />
            <Route path="/my-work" element={<ModuleGuard moduleKey="my-work"><MyWork /></ModuleGuard>} />
            <Route path="/tasks" element={<ModuleGuard moduleKey="tasks"><Tasks /></ModuleGuard>} />
            <Route path="/projects" element={<ModuleGuard moduleKey="projects"><Projects /></ModuleGuard>} />
            <Route path="/projects/:id" element={<ModuleGuard moduleKey="projects"><ProjectDetail /></ModuleGuard>} />
            <Route path="/calendar" element={<ModuleGuard moduleKey="calendar"><CalendarPage /></ModuleGuard>} />
            <Route path="/chat" element={<ModuleGuard moduleKey="chat"><Chat /></ModuleGuard>} />
            <Route path="/time-tracking" element={<ModuleGuard moduleKey="time-tracking"><TimeTracking /></ModuleGuard>} />
            <Route path="/requests" element={<ModuleGuard moduleKey="requests"><Requests /></ModuleGuard>} />
            <Route path="/leave" element={<ModuleGuard moduleKey="leave"><Leave /></ModuleGuard>} />
            <Route path="/notes" element={<ModuleGuard moduleKey="notes"><Notes /></ModuleGuard>} />
            <Route path="/meetings" element={<ModuleGuard moduleKey="meetings"><Meetings /></ModuleGuard>} />
            <Route path="/designs" element={<ModuleGuard moduleKey="designs"><Designs /></ModuleGuard>} />
            <Route path="/purchasing" element={<ModuleGuard moduleKey="purchasing"><Purchasing /></ModuleGuard>} />
            <Route path="/reports" element={<ModuleGuard moduleKey="reports"><Reports /></ModuleGuard>} />
            <Route path="/settings" element={<ModuleGuard moduleKey="settings"><SettingsPage /></ModuleGuard>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}
